import { type ComponentChildren, createContext } from 'preact'
import { useContext, useEffect, useState } from 'preact/hooks'
import { ApiError, api, errorMessage } from '../api/client'
import type { Me } from '../api/generated/Me'
import { unsubscribe } from '../notifications/push'

type Session = {
  /** `undefined` while loading, `null` when logged out. */
  me: Me | null | undefined
  /** Set when `/me` failed for a reason other than being logged out. */
  loadError: string | null
  reload: () => void
  setMe: (me: Me | null) => void
  logout: () => Promise<void>
}

const SessionContext = createContext<Session | null>(null)

export function SessionProvider({ children }: { children: ComponentChildren }) {
  const [me, setMe] = useState<Me | null>()
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = () => {
    setLoadError(null)
    api.get<Me>('/me').then(setMe, (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) setMe(null)
      else setLoadError(errorMessage(error))
    })
  }

  useEffect(reload, [])

  const logout = async () => {
    await unsubscribe().catch(() => {})
    await api.post('/auth/logout')
    setMe(null)
  }

  return (
    <SessionContext.Provider value={{ me, loadError, reload, setMe, logout }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession outside SessionProvider')
  return session
}
